import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import DemoWizardItem from '../../../Domain/DemoWizardItem';

// Window description for the gallery's SIDE-BY-SIDE wizard demo
// (GeneralEditWizzardBaseSideBySideFigma). It is an ordinary wizard window
// description — same statics, same pages/fields, nothing side-by-side-specific
// required — plus the two optional panel headers and the two optional per-field
// keys that mark a field as one-sided.
//
// Modeled on the real CD-5406 use case: left = values an AI extracted from an
// uploaded contract, right = the checklist fields the user reviews and saves.

const CATEGORY_OPTIONS = [
  { label: '-- Select --', value: '' },
  { label: 'Retail', value: 1 },
  { label: 'Hospitality', value: 2 },
  { label: 'Automotive', value: 3 },
];

export default class DemoSideBySideWizardWindowDescription {
  static titleForCreate = 'Review Imported Demo Item';
  static titleForEdit = 'Review Imported Demo Item';
  static entityName = 'Demo Item';
  static itemType = DemoWizardItem;
  static messageOnSuccess = 'Demo item "saved" (in-memory only — nothing was sent to the backend).';
  static finishButtonLabel = 'Save reviewed values';

  // Column headers (both optional — they default to 'Imported values' / 'Values to save').
  static readOnlySideLabel = 'Current Data';

  static editSideLabel = 'AI Extracted Values';

  static serviceFunction = (edited: DemoWizardItem) => Promise.resolve({});

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      description: 'Left: what the extraction returned. Right: what will be saved — correct anything wrong.',
      fields: [
        {
          fieldName: 'Name',
          label: 'Name *',
          className: 'col-12',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.forRequiredTextField,
        },
        {
          fieldName: 'Reference',
          label: 'Client Reference',
          className: 'col-12',
          type: InputTypesEnum.Text,
          // A PLAIN `hide` rule, reading the item — so it is evaluated per column against that column's own
          // data. With two datasets that means it can come out differently on the two sides: where only one
          // column shows it, the other keeps a blank row so the rows stay in step; where NEITHER shows it
          // (open the demo without a readOnlyItem), the field leaves no row on either side at all.
          hide: (item: DemoWizardItem) => !item.Reference,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'CategoryId',
          label: 'Category *',
          className: 'col-12',
          type: InputTypesEnum.Select,
          validityChecker: ValidityHelper.forRequiredSelectField,
          // Nothing extra needed for the read-only panel: it renders this very same Select, so the
          // id resolves to its label there exactly as it does on the editable side.
          selectvalues: CATEGORY_OPTIONS,
        },
        {
          fieldName: 'Description',
          label: 'Description',
          className: 'col-12',
          type: InputTypesEnum.TextBox,
          numberOfRows: 3,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
      ],
    },
    {
      label: 'Details',
      description: 'A field with no extracted counterpart is simply not part of the read-only panel.',
      fields: [
        {
          fieldName: 'StartDate',
          label: 'Start Date',
          className: 'col-12',
          type: InputTypesEnum.Date,
          validityChecker: ValidityHelper.emptyValidityChecker,
        },
        {
          fieldName: 'Budget',
          label: 'Budget',
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'Confirmed',
          label: 'I confirm the values shown are accurate and match the contract *',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          // No imported counterpart, so it is not shown, not validated and not counted on the read-only
          // side — an ordinary hide rule that only applies there. That side keeps the (blank) row, so the
          // columns below it stay aligned, and the row gets no copy button.
          hideOnReadOnlySide: true,
          validityChecker: (value: boolean) => (value ? '' : 'You must confirm the values before saving.'),
        },
      ],
    },
  ];
}
